(() => {
  if (customElements.get('capezio-card-slider')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const resizeObserver = new ResizeObserver((entries) => {
    entries.forEach(({ target }) => {
      target.closest('capezio-card-slider')?.alignSlide();
    });
  });

  class CapezioCardSlider extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this._controller) return;

        this._track = this.querySelector('[data-capezio-slider-track]');
        this._slides = Array.from(this.querySelectorAll('[data-capezio-slider-slide]'));
        this._dots = Array.from(this.querySelectorAll('[data-capezio-slider-dot]'));
        if (!this._track || this._slides.length <= 1) return;

        this._controller = new AbortController();
        const { signal } = this._controller;
        this._index = Math.min(this._index || 0, this._slides.length - 1);
        this._pendingIndex = null;
        this.dataset.sliderInitialized = 'true';

        this.addEventListener('click', (event) => this.onControlClick(event), { signal });
        this.addEventListener('mousedown', (event) => {
          if (event.target.closest('button')) event.stopPropagation();
        }, { signal });
        this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
        this.closest('product-card')?.addEventListener('change', (event) => {
          if (event.target.matches('.product-card__info input[type="radio"]') &&
              event.target.closest('[data-option-position]')) this.select(0);
        }, { signal });
        this._track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
        this._track.addEventListener('pointerdown', () => {
          this._pendingIndex = null;
          if (!this.inProductCarousel()) {
            this.hydrate(this._index - 1);
            this.hydrate(this._index + 1);
          }
        }, { passive: true, signal });

        resizeObserver.observe(this._track);
        this.updateState();
      });
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._controller = null;
      if (this._track) resizeObserver.unobserve(this._track);
      cancelAnimationFrame(this._frame);
      this._frame = null;
      clearTimeout(this._settleTimer);
      delete this.dataset.sliderInitialized;
    }

    inProductCarousel() {
      return Boolean(this.closest('.product-list--carousel')) ||
        Boolean(this.closest('.capezio-image-products__products') &&
          window.matchMedia('(max-width: 699px)').matches);
    }

    hydrate(index) {
      const slide = this._slides[index];
      const template = slide?.querySelector('template[data-capezio-slider-image]');
      if (!template) return;
      const fragment = template.content.cloneNode(true);
      fragment.querySelectorAll('img').forEach((image) => {
        image.setAttribute('loading', 'eager');
      });
      template.replaceWith(fragment);
    }

    select(index) {
      this._index = (index + this._slides.length) % this._slides.length;
      this._pendingIndex = this._index;
      this.hydrate(this._index);
      this.updateState();
      this._track.scrollTo({
        left: this._track.clientWidth * this._index,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    alignSlide() {
      if (!this._track || !this._controller) return;
      this._pendingIndex = null;
      this._track.scrollTo({ left: this._track.clientWidth * this._index, behavior: 'auto' });
    }

    updateState() {
      this._dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this._index);
        dot.setAttribute('aria-pressed', String(index === this._index));
      });
      this._slides.forEach((slide, index) => {
        slide.toggleAttribute('inert', index !== this._index);
        slide.setAttribute('aria-hidden', String(index !== this._index));
      });
    }

    onControlClick(event) {
      const control = event.target.closest('[data-capezio-slider-prev], [data-capezio-slider-next], [data-capezio-slider-dot]');
      if (!control || !this.contains(control)) return;
      event.preventDefault();
      event.stopPropagation();

      if (control.hasAttribute('data-capezio-slider-dot')) {
        this.select(Number(control.dataset.capezioSliderDot));
      } else {
        this.select(this._index + (control.hasAttribute('data-capezio-slider-prev') ? -1 : 1));
      }
    }

    onKeydown(event) {
      if (!event.target.closest('button')) return;
      let index;
      if (event.key === 'ArrowLeft') index = this._index - 1;
      if (event.key === 'ArrowRight') index = this._index + 1;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = this._slides.length - 1;
      if (index === undefined) return;
      event.preventDefault();
      event.stopPropagation();
      this.select(index);
    }

    onScroll() {
      if (!this._frame) {
        this._frame = requestAnimationFrame(() => {
          this._frame = null;
          const width = this._track.clientWidth;
          if (!width) return;
          const position = this._track.scrollLeft / width;
          this.hydrate(Math.floor(position));
          this.hydrate(Math.ceil(position));
          if (this._pendingIndex === null) {
            this._index = Math.max(0, Math.min(Math.round(position), this._slides.length - 1));
            this.updateState();
          }
        });
      }

      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(() => {
        this._pendingIndex = null;
        if (!this._track.clientWidth) return;
        this._index = Math.max(0, Math.min(Math.round(this._track.scrollLeft / this._track.clientWidth), this._slides.length - 1));
        this.updateState();
      }, 120);
    }
  }

  customElements.define('capezio-card-slider', CapezioCardSlider);
})();
